// @react
import React, { useRef } from 'react'
// @typings
import { ContactFormValues } from 'modules/common/forms/types/contact'
// @libraries
import {
  ArrayField,
  Control,
  Controller,
  FieldErrors,
  UseFormMethods,
  useWatch,
} from 'react-hook-form'
// @commons
import {
  ATD_ca_provinces_options,
  ATD_us_states_options,
} from 'modules/common/forms/fields/_config/addresss'
// @components
import Grid from '@bit/atd.web.grid'
import InputText from '@bit/atd.web.forms.input-text'
import Select from '@bit/atd.web.select'
import Hidden from '@material-ui/core/Hidden'

type PropsType = {
  control: Control<ContactFormValues>
  formMethods: UseFormMethods<ContactFormValues>
  index: number
  item: Partial<ArrayField<Record<string, any>, 'id'>>
}

type PropsErrorType = {
  errors: FieldErrors
}

/**
 *
 * @param control
 * @param errors
 * @param index
 * @param item
 * @constructor
 */
const AddressArray = ({
  control,
  errors,
  index,
  item,
}: PropsType & PropsErrorType) => {
  /****************************************
   * HOOKS
   *****************************************/

  const watchCountry = useWatch<string>({
    control,
    name: `addresses[${index}].countryCode`,
  })

  /****************************************
   * REFS
   *****************************************/

  const addressRef = useRef<HTMLInputElement | null>(null)
  const zipCodeRef = useRef<HTMLInputElement | null>(null)
  const cityRef = useRef<HTMLInputElement | null>(null)
  const stateRef = useRef<HTMLInputElement | null>(null)

  /****************************************
   * RENDER
   *****************************************/

  let addressLabel = 'Address 1 / GPO Box'
  let address2Label = 'Address 2'
  let address2LabelWithPeriod = 'Address 2.'
  let address3Label = 'Address 3'
  let postalCodeLabel = 'Post Code'
  let stateLabel = 'State'
  let stateOptions: null | { value: string; label: string }[] = null

  switch (watchCountry) {
    case 'CA':
      addressLabel = 'Street'
      postalCodeLabel = 'Postal Code'
      stateLabel = 'Province'
      stateOptions = ATD_ca_provinces_options
      break
    case 'CN':
      addressLabel = 'Bldg # & Street'
      address3Label = 'District'
      stateLabel = 'Province'
      break
    case undefined:
    case 'US':
      addressLabel = 'Address'
      address2Label = 'Apt, Suite, Etc.'
      address2LabelWithPeriod = 'Apt, Suite, Etc.'
      postalCodeLabel = 'Zip Code'
      stateLabel = 'State'
      stateOptions = ATD_us_states_options
      break
    default:
      addressLabel = 'Address'
      postalCodeLabel = 'Postal Code'
      stateLabel = 'Province/Region'
      break
  }

  return (
    <>
      {/* Address 1 */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          onFocus={() => addressRef?.current?.focus()}
          name={`addresses[${index}].address1`}
          defaultValue={item.address1}
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              required
              name={name}
              label={addressLabel}
              placeholder={`Enter your ${addressLabel.toLowerCase()}.`}
              value={value as string}
              onBlur={onBlur}
              onChange={onChange}
              inputRef={addressRef}
              error={!!errors?.addresses?.[index]?.address1}
              errorText={errors?.addresses?.[index]?.address1?.message}
            />
          )}
        />
      </Grid>

      {/* Address 2 */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name={`addresses[${index}].address2`}
          defaultValue={item.address2}
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              name={name}
              label={address2Label}
              placeholder={`Enter your ${address2LabelWithPeriod.toLowerCase()}`}
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
            />
          )}
        />
      </Grid>

      {/* Address 3 */}
      {watchCountry === 'cn' && (
        <Grid xs={12} sm={6} item>
          <Controller
            control={control}
            name={`addresses[${index}].address3`}
            defaultValue={item.address3}
            render={({ onChange, onBlur, value, name }) => (
              <InputText
                name={name}
                label={address3Label}
                placeholder={`Enter your ${address3Label.toLowerCase()}.`}
                value={value as string}
                onBlur={onBlur}
                onChange={onChange}
              />
            )}
          />
        </Grid>
      )}

      {/* City */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name={`addresses[${index}].city`}
          onFocus={() => cityRef?.current?.focus()}
          defaultValue={item.city}
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              required
              name={name}
              label="City"
              placeholder="Enter your city."
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
              inputRef={cityRef}
              error={!!errors?.addresses?.[index]?.city}
              errorText={errors?.addresses?.[index]?.city?.message}
            />
          )}
        />
      </Grid>

      {/* State */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name={`addresses[${index}].state`}
          defaultValue={item.state}
          onFocus={() => stateRef?.current?.focus()}
          render={({ onChange, onBlur, value, name }) => (
            <>
              {stateOptions !== null ? (
                <Select
                  native
                  required={watchCountry === 'CA' || watchCountry === 'US'}
                  name={name}
                  label={stateLabel}
                  placeholder="Choose your state."
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value as string}
                  inputRef={stateRef}
                  options={stateOptions}
                  error={!!errors?.addresses?.[index]?.state}
                  errorText={errors?.addresses?.[index]?.state?.message}
                />
              ) : (
                <InputText
                  required={watchCountry === 'CA' || watchCountry === 'US'}
                  name={name}
                  label={stateLabel}
                  placeholder={`Enter your ${stateLabel.toLowerCase()}.`}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value as string}
                  inputRef={stateRef}
                  error={!!errors?.addresses?.[index]?.state}
                  errorText={errors?.addresses?.[index]?.state?.message}
                />
              )}
            </>
          )}
        />
      </Grid>

      {/* Postal Code */}
      <Grid xs={12} sm={6} item>
        <Controller
          control={control}
          name={`addresses[${index}].postalCode`}
          defaultValue={item.postalCode}
          onFocus={() => zipCodeRef?.current?.focus()}
          render={({ onChange, onBlur, value, name }) => (
            <InputText
              required
              name={name}
              label={postalCodeLabel}
              placeholder={`Enter your ${postalCodeLabel.toLowerCase()}.`}
              onBlur={onBlur}
              onChange={onChange}
              value={value as string}
              inputRef={zipCodeRef}
              error={!!errors?.addresses?.[index]?.postalCode}
              errorText={errors?.addresses?.[index]?.postalCode?.message}
            />
          )}
        />
      </Grid>

      {/*/!* Hidden Grid Item - Listens for company name *!/*/}
      {/*<HiddenWatchAddressTypeField index={index} control={control} />*/}
    </>
  )
}

export default AddressArray
